export interface FormattedMatrixRow {
  label: string
  value: string
}

export type FormattedAnswer = string | Array<FormattedMatrixRow>

export function isMatrixAnswer(answer: FormattedAnswer): answer is Array<FormattedMatrixRow> {
  return Array.isArray(answer)
}

export function resolveChoiceLabel(question: any, value: string): string {
  if (!question.choices) return value
  const valueLower = value.toLowerCase()

  let choice = question.choices.find((c: any) => {
    const cVal = c.value !== undefined ? c.value : c
    return String(cVal).toLowerCase() === valueLower
  })

  if (!choice) {
    choice = question.choices.find((c: any) => {
      const cText = c.text !== undefined ? c.text : c
      return String(cText).toLowerCase() === valueLower
    })
  }

  if (choice) {
    return choice.text || choice.value || choice
  }
  return value
}

export function resolveRowLabel(question: any, rowKey: string): string {
  if (!question.rows) return rowKey
  const rowKeyLower = rowKey.toLowerCase()

  let rowObj = question.rows.find(
    (r: any) => String(r.value !== undefined ? r.value : r).toLowerCase() === rowKeyLower,
  )
  if (!rowObj) {
    rowObj = question.rows.find(
      (r: any) => String(r.text !== undefined ? r.text : r).toLowerCase() === rowKeyLower,
    )
  }
  return rowObj ? (rowObj.text || rowObj.value || rowKey) : rowKey
}

export function resolveColumnLabel(question: any, colKey: string): string {
  if (!question.columns) return colKey
  const colKeyLower = colKey.toLowerCase()

  let colObj = question.columns.find(
    (c: any) => String(c.name || c.value || c).toLowerCase() === colKeyLower,
  )
  if (!colObj) {
    colObj = question.columns.find(
      (c: any) => String(c.title || c.text || c).toLowerCase() === colKeyLower,
    )
  }
  return colObj ? (colObj.title || colObj.text || colObj.name || colObj.value || colKey) : colKey
}

export function resolveCellValueLabel(question: any, columnKey: string, cellValue: string): string {
  if (!question.columns) return cellValue

  const column = question.columns.find((c: any) => {
    const colKey = c?.name || c?.value || c
    return String(colKey).toLowerCase() === columnKey.toLowerCase()
  })

  if (!column || !Array.isArray(column.choices)) return cellValue

  const target = cellValue.toLowerCase()
  const choice =
    column.choices.find((c: any) => String(c?.value ?? c).toLowerCase() === target) ||
    column.choices.find((c: any) => String(c?.text ?? c).toLowerCase() === target)

  return choice ? String(choice.text ?? choice.value ?? cellValue) : cellValue
}

function resolveRateValueLabel(question: any, value: string): string {
  if (!Array.isArray(question.rateValues)) return value
  const valueLower = value.toLowerCase()
  const rate = question.rateValues.find(
    (r: any) => String(r.value !== undefined ? r.value : r).toLowerCase() === valueLower,
  )
  return rate ? (rate.text || String(rate.value) || value) : value
}

function formatMatrixValue(question: any, matrixData: Record<string, any>): Array<FormattedMatrixRow> {
  return Object.entries(matrixData).map(([rowKey, colVal]) => ({
    label: resolveRowLabel(question, rowKey),
    value: resolveColumnLabel(question, String(colVal)),
  }))
}

function getColumnCellType(question: any, colKey: string): string {
  if (!question?.columns) return ''
  const col = question.columns.find((c: any) => {
    const k = c?.name || c?.value || c
    return String(k).toLowerCase() === colKey.toLowerCase()
  })
  return col?.cellType || question.cellType || ''
}

const RATING_CELL_TYPES = new Set(['dropdown', 'rating', 'radiogroup', 'boolean'])

function formatMatrixDropdownValue(
  question: any,
  matrixData: Record<string, Record<string, any>>,
): Array<FormattedMatrixRow> {
  return Object.entries(matrixData).map(([rowKey, colRatings]) => {
    const entries = Object.entries(colRatings as Record<string, any>)
    const hasTextColumn = entries.some(
      ([colKey]) => !RATING_CELL_TYPES.has(getColumnCellType(question, colKey)),
    )

    if (hasTextColumn) {
      const textParts = entries
        .filter(([colKey]) => !RATING_CELL_TYPES.has(getColumnCellType(question, colKey)))
        .map(([colKey, val]) => resolveCellValueLabel(question, colKey, String(val)))
      return {
        label: resolveRowLabel(question, rowKey),
        value: textParts.join(', '),
      }
    }

    const ratingParts = entries.map(([colKey, val]) => {
      const colLabel = resolveColumnLabel(question, colKey)
      const valLabel = resolveCellValueLabel(question, colKey, String(val))
      return `${colLabel}: ${valLabel}`
    })
    return {
      label: resolveRowLabel(question, rowKey),
      value: ratingParts.join(', '),
    }
  })
}

export function formatSurveyAnswer(question: any, value: any): FormattedAnswer {
  if (value === null || value === undefined) return '—'

  const type = question?.type || ''

  switch (type) {
    case 'radiogroup':
    case 'dropdown':
      return resolveChoiceLabel(question, String(value))

    case 'boolean':
      return value === true || value === 'true' ? 'Yes' : 'No'

    case 'rating':
      return resolveRateValueLabel(question, String(value))

    case 'text':
    case 'comment':
      return String(value)

    case 'checkbox': {
      const items = Array.isArray(value)
        ? value
        : String(value).split(',').map((v) => v.trim()).filter(Boolean)
      return items.map((v) => resolveChoiceLabel(question, String(v))).join(', ')
    }

    case 'ranking': {
      const ranked = Array.isArray(value) ? value : [value]
      return ranked
        .map((v, i) => `${i + 1}. ${resolveChoiceLabel(question, String(v))}`)
        .join(', ')
    }

    case 'matrix': {
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        return formatMatrixValue(question, value)
      }
      return String(value)
    }

    case 'matrixdropdown':
    case 'matrixdynamic': {
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        return formatMatrixDropdownValue(question, value)
      }
      return String(value)
    }

    default:
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value)
        } catch {
          return String(value)
        }
      }
      return String(value)
  }
}
